import React from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowRight } from "lucide-react";
import { FEATURES } from "../lib/features";
import { useContactDialog } from "../context/ContactDialogContext";

const Features = () => {
  const { openDialog } = useContactDialog();
  return (
    <div data-testid="page-features">
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
            Funktioner i detalj
          </span>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl text-white leading-[1.05]">
            Fjorton moduler. <span className="italic gold-text">Ett system.</span>
          </h1>
          <p className="mt-6 text-white/70 leading-relaxed max-w-2xl mx-auto">
            Här är hela bredden av vad Orkesteradmin kan göra för din verksamhet.
            Varje modul är byggd för att hantera en specifik del av orkesterns
            arbete – och alla moduler hänger ihop.
          </p>
        </div>
      </section>

      <section className="pb-32" data-testid="features-detailed-list">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 space-y-8">
          {FEATURES.map((f, idx) => {
            const Icon = LucideIcons[f.icon] || LucideIcons.Sparkles;
            return (
              <div
                key={f.key}
                className="glass-card rounded-2xl p-8 md:p-10 grid md:grid-cols-12 gap-8 items-start"
                data-testid={`feature-detail-${f.key}`}
              >
                <div className="md:col-span-4 flex md:block items-center gap-4">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 mb-0 md:mb-5">
                    <Icon className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/40">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-white mt-1 leading-tight">
                      {f.title}
                    </h2>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <p className="text-white/80 text-lg leading-relaxed">{f.short}</p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {f.details.map((d, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-white/70 text-sm"
                      >
                        <span className="mt-2 h-1 w-1 rounded-full bg-[#d4af37] shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  {f.links && f.links.length > 0 && (
                    <ul className="mt-6 flex flex-wrap gap-3" data-testid={`feature-links-${f.key}`}>
                      {f.links.map((link) => (
                        <li key={link.url}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`feature-link-${f.key}-${link.label.toLowerCase()}`}
                            className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-white border border-[#d4af37]/30 hover:border-[#d4af37] rounded-full px-4 py-2 transition-all"
                          >
                            {link.label}
                            <LucideIcons.ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-10 mt-20 text-center">
          <h3 className="font-serif text-3xl text-white">
            Vill du se det live?
          </h3>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">
            Boka en personlig demo och vi går igenom modulerna utifrån era behov.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              type="button"
              onClick={() => openDialog("demo")}
              data-testid="features-page-cta-demo"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full"
            >
              Boka demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/priser"
              data-testid="features-page-cta-pricing"
              className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full"
            >
              Se pris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
